import { APIObject } from '@/shared/plugins/Api/API'
import moment from 'moment'

// The service answers dates as 'YYYY-MM-DD HH:mm:ss', which Date.parse reads as
// local time — the zone the control center shows them in. null when absent.
function parseDate (value) {
  const ms = Date.parse(value)
  return Number.isFinite(ms) ? ms : null
}

// A Prometheus duration ('30s', '5m', '1h', '1d') in seconds, or null when
// there is nothing to read.
const DURATION_UNITS = { s: 1, m: 60, h: 3600, d: 86400 }
function parsePrometheusDuration (value) {
  const match = /^(\d+)([smhd])$/.exec(String(value || ''))
  return match ? parseInt(match[1], 10) * DURATION_UNITS[match[2]] : null
}

// Unix seconds -> the nanosecond timestamp the metrics service expects. Built
// as a string so the value never passes through a float.
function toNanoseconds (seconds) {
  if (!Number.isFinite(seconds)) return undefined
  return `${Math.floor(seconds)}000000000`
}

// The payload nests the list under `panels_<service>`. Fall back to the first
// array the body carries, so a renamed key degrades to a working chart rather
// than an empty one.
function readPanels (body, service) {
  const data = body?.data
  if (!data || typeof data !== 'object') return []
  const named = data[`panels_${service}`]
  if (Array.isArray(named)) return named
  return Object.values(data).find(value => Array.isArray(value)) || []
}

class Metrics extends APIObject {
  constructor (options) {
    // Init
    super('CC', options)
  }

  async appsRps (type) {
    const response = await this.request(
      {
        method: 'get',
        url: '/v1/metrics/overview/apps/rps',
        params: {
          start: moment().subtract(1, 'hours').unix(),
          end: moment().unix(),
          type
        }
      }
    )

    return response.data?.[0]?.value?.[1]
  }

  /**
   * Every pod a service ran over a window, with the application each belongs to.
   *
   * Unlike the appservice monitoring, which only knows the pods alive right now,
   * this answers with the pods that ran at any point in the window. A chart fed
   * from it keeps the history of an application that was restarted, redeployed
   * or stopped since.
   *
   * @param {string} service 'app' | 'api' | 'superset'
   * @param {{ start: number, end: number }} window unix seconds
   * @returns {Promise<Array<{ name: string, appName: string, displayName: string }>>}
   *   one entry per pod, deduplicated by pod name
   */
  async servicePods (service, { start, end } = {}) {
    const body = await this.request({
      method: 'get',
      url: `/v1/metrics/services/${encodeURIComponent(service)}`,
      params: {
        start_at: toNanoseconds(start),
        end_at: toNanoseconds(end)
      }
    })

    const pods = []
    const seen = new Set()
    for (const panel of readPanels(body, service)) {
      // app_name is the grouping key: display_name is not unique, two services
      // can both hold a "finops".
      const appName = panel?.app_name
      if (!appName) continue
      // `date` carries each pod's own window; without it a caller reading a
      // wide range cannot tell which pods were alive in a narrower one.
      const windows = {}
      for (const entry of panel.date || []) {
        if (entry?.name) windows[entry.name] = entry
      }

      for (const name of panel.pods || []) {
        if (!name || seen.has(name)) continue
        seen.add(name)
        const ran = windows[name]
        pods.push({
          name,
          appName,
          displayName: panel.display_name || appName,
          from: parseDate(ran?.start_at),
          // No end means the pod is still up.
          to: parseDate(ran?.end_at)
        })
      }
    }
    return pods
  }

  /**
   * One metric for every pod of an application, in a single call.
   *
   * The per-pod endpoint below costs one request per pod; this one matches
   * `pod=~"<appName>-.*"` server side and answers `sum by (pod)`, so the shape
   * is the same but a busy project no longer means hundreds of requests.
   *
   * It only knows the pods named after the application. A cache sidecar and a
   * build pod carry their own prefixes, so they are asked for separately — see
   * fetchAppMetrics.
   *
   * @returns {Promise<{ series: Array<{ pod: string, data: Array<[number, number]> }>, step: number|null }>}
   *   `step` is the resolution the server actually used: it widens one too fine
   *   for the range, and filling gaps on the asked-for grid instead would comb
   *   the curve with zeros between real points.
   */
  async appMetricChart (appName, metricKey, { start, end, step } = {}) {
    const response = await this.request({
      method: 'get',
      url: `/v1/metrics/apps/${encodeURIComponent(appName)}/${encodeURIComponent(metricKey)}`,
      params: { start, end, step },
      originalResponse: true
    })

    const result = response?.data?.data
    return {
      step: parsePrometheusDuration(response?.headers?.['x-metrics-step']),
      series: (Array.isArray(result) ? result : []).map(entry => ({
        pod: entry?.metric?.pod || '',
        data: (entry?.values || []).map(v => [parseInt(v[0]), parseFloat(v[1]) || 0])
      }))
    }
  }

  async podMetricChart (podId, metricKey, { start, end, step } = {}) {
    const response = await this.request({
      method: 'get',
      url: `/v1/metrics/services/${encodeURIComponent(podId)}/${encodeURIComponent(metricKey)}`,
      params: { start, end, step }
    })
    // response.data is an array of { values: [[timestamp, valueString], ...] }
    const series = response?.data || []
    if (!series.length || !series[0]?.values) return []
    return series[0].values.map(v => [parseInt(v[0]), parseFloat(v[1]) || 0])
  }

  // TODO: This endpoint is no more managed by CMP team. Need to remove this once we replace where it is used (the cc's metrics on the project dashboard)
  async alertingTriggerings (alertId = null, startEnd) {
    const params = {
      ...startEnd
    }
    if (alertId) {
      params.alertId = alertId
    }

    const response = await this.request(
      {
        method: 'get',
        url: '/v1/metrics/overview/alerting/triggerings',
        params
      }
    )

    return response.data?.[0]?.value?.[1]
  }
}

export default Metrics
export {
  Metrics
}
